import React, { useState } from "react";
import { 
  Search, 
  Image as ImageIcon, 
  Sparkles, 
  Check, 
  Upload, 
  Loader2, 
  ExternalLink,
  Tag,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from "@/components/ui/dialog";
import { toast } from "sonner";

interface StockImage {
  id: string;
  title: string;
  category: string;
  keywords: string[];
  url: string;
  author: string;
  authorUrl: string;
}

const LEGAL_STOCK_IMAGES: StockImage[] = [
  // IA & Legal Tech
  {
    id: "legal-ai-brain",
    title: "Inteligencia Artificial y Redes Neurales en Derecho",
    category: "IA & Legal Tech",
    keywords: ["ia", "inteligencia artificial", "algoritmos", "tecnologia", "futuro", "datos"],
    url: "https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?q=80&w=1200&auto=format&fit=crop",
    author: "Milad Fakurian",
    authorUrl: "https://unsplash.com/@fakurian"
  },
  {
    id: "legal-tech-laptop",
    title: "Abogado Investigando con IA y Computadora",
    category: "IA & Legal Tech",
    keywords: ["abogado", "laptop", "investigacion", "tecnologia", "despacho"],
    url: "https://images.unsplash.com/photo-1488590528505-98d2b5aba04b?q=80&w=1200&auto=format&fit=crop",
    author: "Luca Bravo",
    authorUrl: "https://unsplash.com/@lucabravo"
  },
  {
    id: "digital-justice",
    title: "Báscula de la Justicia Digitalizada",
    category: "IA & Legal Tech",
    keywords: ["justicia", "balanza", "digital", "derecho", "corte"],
    url: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?q=80&w=1200&auto=format&fit=crop",
    author: "Tingey Injury Law Firm",
    authorUrl: "https://unsplash.com/@tingeylawfirm"
  },

  // Leyes & Justicia
  {
    id: "gavel-justice",
    title: "Mazo de Juez y Libros de Jurisprudencia",
    category: "Leyes & Justicia",
    keywords: ["mazo", "juez", "sentencia", "corte", "tutela", "jurisprudencia"],
    url: "https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop",
    author: "Giammarco",
    authorUrl: "https://unsplash.com/@giammarco"
  },
  {
    id: "legal-books-library",
    title: "Biblioteca de Códigos y Leyes Colombiana",
    category: "Leyes & Justicia",
    keywords: ["libros", "biblioteca", "codigos", "leyes", "investigacion"],
    url: "https://images.unsplash.com/photo-1505664194779-8beaceb93744?q=80&w=1200&auto=format&fit=crop",
    author: "Freddie Marriage",
    authorUrl: "https://unsplash.com/@freddiemarriage"
  },
  {
    id: "courtroom-pillars",
    title: "Palacio de Justicia y Columnas Románicas",
    category: "Leyes & Justicia",
    keywords: ["corte", "palacio", "justicia", "rama judicial", "tribunal"],
    url: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?q=80&w=1200&auto=format&fit=crop",
    author: "Samuel Pele",
    authorUrl: "https://unsplash.com/@samuelpele"
  },

  // Datos & Ciberseguridad (Ley 1581)
  {
    id: "data-privacy-lock",
    title: "Protección de Datos Personales y Ciberseguridad",
    category: "Datos & Seguridad",
    keywords: ["datos", "privacidad", "seguridad", "ley 1581", "sic", "candado"],
    url: "https://images.unsplash.com/photo-1550751827-4bd374c3f58b?q=80&w=1200&auto=format&fit=crop",
    author: "Tobias Tullius",
    authorUrl: "https://unsplash.com/@tobiastullius"
  },
  {
    id: "cloud-data-analytics",
    title: "Servidores en la Nube y Análisis de Expedientes",
    category: "Datos & Seguridad",
    keywords: ["nube", "servidores", "expedientes", "big data", "monitoreo"],
    url: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?q=80&w=1200&auto=format&fit=crop",
    author: "Luke Chesser",
    authorUrl: "https://unsplash.com/@lukechesser"
  },

  // Calculadoras & Finanzas Jurídicas
  {
    id: "calculator-finance-legal",
    title: "Calculadora, Monedas y Liquidaciones Laborales",
    category: "Calculadoras & Finanzas",
    keywords: ["calculadora", "cuantificacion", "liquidaciones", "laboral", "ipc", "dane"],
    url: "https://images.unsplash.com/photo-1554224155-8d04cb21cd6c?q=80&w=1200&auto=format&fit=crop",
    author: "Stephen Dawson",
    authorUrl: "https://unsplash.com/@stephendawson"
  },
  {
    id: "financial-charts-growth",
    title: "Gráficos de Perjuicios Civiles y Tasas DTF",
    category: "Calculadoras & Finanzas",
    keywords: ["graficos", "intereses", "superfinanciera", "perjuicios", "indexacion"],
    url: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?q=80&w=1200&auto=format&fit=crop",
    author: "Carlos Muza",
    authorUrl: "https://unsplash.com/@kmuza"
  },

  // Despachos & Firma
  {
    id: "law-firm-meeting",
    title: "Reunión Estratégica en Despacho de Abogados",
    category: "Despachos & Firmas",
    keywords: ["despacho", "firma", "abogados", "reunion", "cliente"],
    url: "https://images.unsplash.com/photo-1521791136064-7986c2920216?q=80&w=1200&auto=format&fit=crop",
    author: "Cytonn Photography",
    authorUrl: "https://unsplash.com/@cytonn_photography"
  },
  {
    id: "lawyer-handshake",
    title: "Acuerdo Legal y Firma de Contrato",
    category: "Despachos & Firmas",
    keywords: ["acuerdo", "firma", "contrato", "negociacion", "socios"],
    url: "https://images.unsplash.com/photo-1560250097-0b93528c311a?q=80&w=1200&auto=format&fit=crop",
    author: "LinkedIn Sales Solutions",
    authorUrl: "https://unsplash.com/@linkedinsalesnavigator"
  },

  // Documentos & Contratos
  {
    id: "contract-signing-pen",
    title: "Redacción y Firma Digital de Minuta Contractual",
    category: "Contratos & Documentos",
    keywords: ["contrato", "pluma", "firma", "minuta", "redaccion"],
    url: "https://images.unsplash.com/photo-1455390582262-044cdead277a?q=80&w=1200&auto=format&fit=crop",
    author: "Scott Graham",
    authorUrl: "https://unsplash.com/@scottgraham"
  },
  {
    id: "bulk-process-ingest",
    title: "Carga Masiva de Radicados y Expedientes Judiciales",
    category: "Contratos & Documentos",
    keywords: ["masiva", "expedientes", "samai", "cpnu", "tyba", "ingesta"],
    url: "https://images.unsplash.com/photo-1450133064473-71024230f91b?q=80&w=1200&auto=format&fit=crop",
    author: "Unsplash Legal",
    authorUrl: "https://unsplash.com"
  }
];

const CATEGORIES = [
  "Todos",
  "IA & Legal Tech",
  "Leyes & Justicia",
  "Datos & Seguridad",
  "Calculadoras & Finanzas",
  "Despachos & Firmas",
  "Contratos & Documentos"
];

interface UnsplashStockGalleryModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectCoverImage?: (url: string) => void;
  onInsertMarkdownImage?: (url: string, title: string) => void;
}

export const UnsplashStockGalleryModal: React.FC<UnsplashStockGalleryModalProps> = ({
  isOpen,
  onClose,
  onSelectCoverImage,
  onInsertMarkdownImage,
}) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("Todos");
  const [customUrl, setCustomUrl] = useState("");

  const filteredImages = LEGAL_STOCK_IMAGES.filter((img) => {
    const matchesCategory = selectedCategory === "Todos" || img.category === selectedCategory;
    const q = searchQuery.toLowerCase().trim();
    const matchesSearch = !q || 
      img.title.toLowerCase().includes(q) || 
      img.category.toLowerCase().includes(q) || 
      img.keywords.some((k) => k.toLowerCase().includes(q));
    return matchesCategory && matchesSearch;
  });

  const handleSelectImage = (img: StockImage, action: "cover" | "insert") => {
    if (action === "cover" && onSelectCoverImage) {
      onSelectCoverImage(img.url);
      toast.success(`Portada '${img.title}' seleccionada.`);
      onClose();
    } else if (action === "insert" && onInsertMarkdownImage) {
      onInsertMarkdownImage(img.url, img.title);
      toast.success(`Imagen insertada en el texto.`);
      onClose();
    }
  };

  const handleApplyCustomUrl = (action: "cover" | "insert") => {
    if (!customUrl || !customUrl.startsWith("http")) {
      toast.warning("Ingresa una URL válida de imagen (http:// o https://).");
      return;
    }

    if (action === "cover" && onSelectCoverImage) {
      onSelectCoverImage(customUrl);
      toast.success("URL de portada personalizada aplicada.");
      onClose();
    } else if (action === "insert" && onInsertMarkdownImage) {
      onInsertMarkdownImage(customUrl, "Imagen de Stock");
      toast.success("URL de imagen insertada en el texto.");
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[850px] max-h-[90vh] rounded-[2rem] border-none shadow-2xl p-6 bg-white dark:bg-slate-900 flex flex-col overflow-hidden">
        <DialogHeader className="pb-2">
          <DialogTitle className="text-xl font-black text-slate-900 dark:text-white flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-pink-500" />
              <span>Galería de Imágenes Legal & Tech (Unsplash HD)</span>
            </div>
            <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-400 text-xs font-semibold">
              Libre de Derechos / Alta ResoluciÃ³n
            </Badge>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500 dark:text-slate-400">
            Explora y selecciona fotografÃas profesionales de stock para portadas de boletines o imÃ¡genes ilustrativas dentro de tus artÃculos.
          </DialogDescription>
        </DialogHeader>

        {/* Search Bar & Categories */}
        <div className="space-y-3 py-2">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3.5 top-1/2 transform -translate-y-1/2 text-slate-400" />
            <Input
              type="text"
              placeholder="Buscar por tema (ej. IA, tutela, derecho laboral, contratos, SIC, calculadora)..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-10 rounded-xl bg-slate-50 dark:bg-slate-950 border-slate-200 dark:border-slate-800 text-xs"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery("")} 
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
            {CATEGORIES.map((cat) => (
              <button
                key={cat}
                type="button"
                onClick={() => setSelectedCategory(cat)}
                className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap transition-all ${
                  selectedCategory === cat
                    ? "bg-pink-600 text-white shadow-md shadow-pink-500/20"
                    : "bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200"
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        </div>

        {/* Image Grid */}
        <div className="flex-1 overflow-y-auto pr-1 py-2 min-h-[300px]">
          {filteredImages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <ImageIcon className="w-12 h-12 text-slate-300 mb-3" />
              <p className="text-sm font-bold text-slate-700 dark:text-slate-300">No se encontraron imÃ¡genes</p>
              <p className="text-xs text-slate-500">Prueba con otros tÃ©rminos o selecciona la categorÃa 'Todos'.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredImages.map((img) => (
                <div
                  key={img.id}
                  className="group relative rounded-2xl overflow-hidden border border-slate-200 dark:border-slate-800 bg-slate-900 shadow-sm hover:shadow-xl transition-all duration-300 flex flex-col"
                >
                  <div className="h-36 overflow-hidden relative">
                    <img
                      src={img.url}
                      alt={img.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 opacity-90 group-hover:opacity-100"
                    />
                    <span className="absolute top-2 left-2 bg-black/60 backdrop-blur-md text-white text-[10px] font-semibold px-2.5 py-0.5 rounded-full">
                      {img.category}
                    </span>
                  </div>

                  <div className="p-3 bg-white dark:bg-slate-900 flex-1 flex flex-col justify-between">
                    <h4 className="text-xs font-bold text-slate-800 dark:text-slate-200 line-clamp-2 mb-3">
                      {img.title}
                    </h4>

                    <div className="flex items-center gap-1.5 pt-2 border-t border-slate-100 dark:border-slate-800 mt-auto">
                      {onSelectCoverImage && (
                        <Button
                          type="button"
                          size="sm"
                          onClick={() => handleSelectImage(img, "cover")}
                          className="flex-1 text-[11px] h-8 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-800 dark:hover:bg-slate-700 rounded-xl font-bold"
                        >
                          Portada
                        </Button>
                      )}
                      
                      {onInsertMarkdownImage && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => handleSelectImage(img, "insert")}
                          className="flex-1 text-[11px] h-8 border-pink-200 text-pink-600 hover:bg-pink-50 dark:hover:bg-pink-950/30 rounded-xl font-bold"
                        >
                          Insertar Texto
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Custom URL Input Fallback */}
        <div className="pt-3 border-t border-slate-200 dark:border-slate-800 flex flex-col sm:flex-row items-center gap-2">
          <Input
            placeholder="O pega directamente una URL de imagen externa (http://...)..."
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            className="rounded-xl text-xs flex-1"
          />
          <div className="flex items-center gap-2 w-full sm:w-auto">
            {onSelectCoverImage && (
              <Button
                type="button"
                size="sm"
                onClick={() => handleApplyCustomUrl("cover")}
                className="text-xs rounded-xl bg-slate-900 text-white font-bold"
              >
                Usar Portada
              </Button>
            )}
            {onInsertMarkdownImage && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => handleApplyCustomUrl("insert")}
                className="text-xs rounded-xl text-pink-600 border-pink-200 font-bold"
              >
                Insertar en Texto
              </Button>
            )}
          </div>
        </div>

        <DialogFooter className="pt-2">
          <Button variant="ghost" onClick={onClose} className="rounded-xl text-xs">
            Cerrar Galería
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
export default UnsplashStockGalleryModal;
